import React from 'react';
import Modal from './Modal';

export interface ChannelPreviewData {
  title: string;
  subtitle: string;
  body: string;
  tags: string[];
  canonicalUrl: string;
  wordCount: number;
  diagrams: Array<{ index: number; imageUrl: string | null }>;
  evals: Array<{ id: string; severity: 'pass' | 'warn' | 'fail'; message: string }>;
  draft: boolean;
}

interface Props {
  channel: 'dev-to' | 'hashnode';
  data: ChannelPreviewData;
  saving: boolean;
  onSaveDraft: () => void;
  onClose: () => void;
}

const CHANNEL_LABEL: Record<string, string> = { 'dev-to': 'Dev.to', hashnode: 'Hashnode' };

export default function ChannelPreviewModal({ channel, data, saving, onSaveDraft, onClose }: Props) {
  const label = CHANNEL_LABEL[channel] || channel;
  const failCount = data.evals.filter((e) => e.severity === 'fail').length;
  const warnCount = data.evals.filter((e) => e.severity === 'warn').length;
  const passCount = data.evals.filter((e) => e.severity === 'pass').length;
  const blocked = failCount > 0;

  return (
    <Modal open onClose={onClose} title={`${label} Preview`} maxWidth={960} hideHeader>
      <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <h3 className="modal__title" style={{ margin: 0 }}>{label} Preview</h3>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
              Saved as a draft on {label} after you confirm. Nothing goes live.
            </div>
          </div>
          <button onClick={onClose} className="btn btn-secondary btn--sm" aria-label="Close">{'✕'}</button>
        </div>

        {/* Eval summary */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, fontSize: 12 }}>
          <span style={{ background: '#E8F5E9', color: '#1B5E20', padding: '4px 10px', borderRadius: 0 }}>{'✓'} {passCount} pass</span>
          {warnCount > 0 && <span style={{ background: '#FFF7E6', color: '#9A6300', padding: '4px 10px', borderRadius: 0 }}>{'⚠'} {warnCount} warn</span>}
          {failCount > 0 && <span style={{ background: 'var(--danger-light)', color: 'var(--danger)', padding: '4px 10px', borderRadius: 0 }}>{'✗'} {failCount} fail</span>}
          <span style={{ color: 'var(--text-tertiary)' }}>· {data.wordCount} words · {data.tags.length} tags · {data.diagrams.length} diagrams</span>
        </div>

        {/* Title + subtitle */}
        <div style={{ padding: '12px 14px', background: '#FAF7EC', border: '1px solid #D9D3BF', marginBottom: 12 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#7B8AA0', marginBottom: 4 }}>Title</div>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{data.title}</div>
          {data.subtitle && (
            <>
              <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#7B8AA0', marginBottom: 4 }}>Subtitle / Description</div>
              <div style={{ fontSize: 13, color: '#3D4756' }}>{data.subtitle}</div>
            </>
          )}
        </div>

        {/* Tags + canonical */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {data.tags.map((t) => (
            <span key={t} style={{ background: '#0E1525', color: '#F2EAD8', padding: '3px 10px', fontSize: 12 }}>#{t}</span>
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>
          Canonical: <a href={data.canonicalUrl} target="_blank" rel="noopener" style={{ color: '#1565C0' }}>{data.canonicalUrl}</a>
        </div>

        {/* Eval rows */}
        <details style={{ marginBottom: 12 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Eval details ({data.evals.length})</summary>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {data.evals.map((e) => {
              const color = e.severity === 'pass' ? '#1B5E20' : e.severity === 'warn' ? '#9A6300' : 'var(--danger)';
              const icon = e.severity === 'pass' ? '✓' : e.severity === 'warn' ? '⚠' : '✗';
              return (
                <div key={e.id} style={{ fontSize: 12, color, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                  {icon} <strong>{e.id}</strong>: {e.message}
                </div>
              );
            })}
          </div>
        </details>

        {/* Transformed body preview */}
        <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#7B8AA0', marginBottom: 4 }}>Transformed content ({label} style)</div>
        <pre style={{
          background: '#0E1525', color: '#F2EAD8', padding: 14, fontSize: 12,
          fontFamily: 'ui-monospace, Menlo, monospace', maxHeight: 360, overflow: 'auto',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 16,
        }}>{data.body}</pre>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={onSaveDraft}
            disabled={saving || blocked}
            title={blocked ? 'Fix failing evals before saving' : `Save as draft on ${label}`}
          >
            {saving ? 'Saving…' : `Save as Draft on ${label}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
